"use client";

import { useTranslations } from "next-intl";
import { Mail, Phone, Loader2 } from "lucide-react";
import { FacebookIcon } from "@/components/icons/facebook-icon";
import { GoogleSignInButton } from "@/components/auth/google-signin-button";
import { cn } from "@/lib/utils";

type Method = "facebook" | "email" | "phone";

interface AuthMethodPickerProps {
  mode: "login" | "register";
  onSelect: (method: Method) => void;
  /** Called with the JWT ID token when Google sign-in completes. */
  onGoogleCredential: (credential: string) => void;
  loadingMethod?: string | null;
  isFacebookReady: boolean;
  error?: { message?: string } | null;
}

interface OptionProps {
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

function Option({ icon, label, hint, onClick, disabled, loading }: OptionProps) {
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      aria-disabled={disabled || undefined}
      className={cn(
        "auth-method-option flex w-full min-h-[5.5rem] cursor-pointer items-center gap-4 rounded-xl border border-border bg-card p-4",
        "text-left transition-colors",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
        {loading ? <Loader2 className="size-5 animate-spin text-muted-foreground" /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold leading-tight">{label}</span>
        <span className="block text-sm text-muted-foreground">{hint}</span>
      </span>
    </div>
  );
}

export function AuthMethodPicker({
  mode,
  onSelect,
  onGoogleCredential,
  loadingMethod,
  isFacebookReady,
  error,
}: AuthMethodPickerProps) {
  const t = useTranslations("auth");
  const busy = !!loadingMethod;

  const emailLabel = mode === "login" ? t("signInWithEmail") : t("signUpWithEmail");
  const phoneLabel = mode === "login" ? t("signInWithPhone") : t("signUpWithPhone");

  return (
    <div className="space-y-3">
      {/* Google — renders the real GIS button for reliable sign-in */}
      <GoogleSignInButton
        onCredential={onGoogleCredential}
        className="w-full"
      />

      {isFacebookReady && (
        <Option
          icon={<FacebookIcon className="size-5" />}
          label={t("continueWithFacebook")}
          hint={t("facebookHint")}
          onClick={() => onSelect("facebook")}
          disabled={busy}
          loading={loadingMethod === "facebook"}
        />
      )}

      <Option
        icon={<Mail className="size-5 text-muted-foreground" />}
        label={emailLabel}
        hint={t("emailHint")}
        onClick={() => onSelect("email")}
        disabled={busy}
      />

      <Option
        icon={<Phone className="size-5 text-muted-foreground" />}
        label={phoneLabel}
        hint={t("phoneHint")}
        onClick={() => onSelect("phone")}
        disabled={busy}
      />

      {error && (
        <p className="text-sm text-destructive">
          {error.message || t("socialAuthError", { provider: "" })}
        </p>
      )}
    </div>
  );
}
